export interface HelpGuide {
  id: string;
  title: string;
  location: string;
  routePatterns: string[];
  category: 'landing' | 'home' | 'agents' | 'workflows' | 'ambient' | 'secure-conversations' | 'admin' | 'rag' | 'settings';
  audience: string;
  status: 'published' | 'draft';
  loomUrl: string | null;
  readTime: string;
  purpose: string;
  talkingPoints: string[];
  writtenGuide: string[];
}

export const helpGuides: HelpGuide[] = [
  {
    id: 'landing-overview',
    title: 'What OrchestratorAI Enterprise Is',
    location: 'Public home page',
    routePatterns: ['/'],
    category: 'landing',
    audience: 'Prospects and first-time evaluators',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Orient a new visitor to the platform without requiring them to understand every module.',
    talkingPoints: [
      'OrchestratorAI Enterprise is a deployable AI operations platform, not a single chatbot.',
      'The platform combines agents, workflows, ambient automation, secure external communication, RAG, admin, and observability.',
      'The real value is adapting the foundation to a customer organization, its data, and its operating rules.',
    ],
    writtenGuide: [
      'OrchestratorAI Enterprise is the platform foundation for running useful AI inside an organization. It is built around production concerns: authentication, roles, entitlements, model configuration, knowledge retrieval, event logs, and operational visibility.',
      'The product areas map to different kinds of work. Agents handle direct expert-style conversations. Workflows coordinate multi-step processes. Ambient watches for events and acts in the background. Secure Conversations manages agent-to-agent communication with outside systems. RAG manages source knowledge. Admin and Settings govern the whole platform.',
      'The important idea is that these pieces are meant to work together. A customer can start with a simple agent, grow into a workflow, connect documents through RAG, monitor activity through observability, and govern access through admin controls.',
      'Use this page as the top-level framing. If a user needs detail, the in-app Help button explains the exact page they are on.',
    ],
  },
  {
    id: 'landing-features',
    title: 'Platform Features Tour',
    location: 'Public features page',
    routePatterns: ['/features'],
    category: 'landing',
    audience: 'Technical and business evaluators',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Explain the platform pillars in a way that connects features to business use.',
    talkingPoints: [
      'Agents, workflows, ambient automation, secure conversations, RAG, and admin solve different problems.',
      'The features are operational pieces of one platform, not separate demos.',
      'The question to ask is which business process each feature should support.',
    ],
    writtenGuide: [
      'The features page should help a buyer or evaluator understand the shape of the platform. Agents are best for direct work with a specialist. Workflows are best for repeatable multi-step processes. Ambient automation is best for event-driven background work. Secure Conversations is best for controlled external A2A communication.',
      'RAG is the knowledge layer. It lets agents and workflows retrieve from managed document collections instead of relying only on model memory. Admin, Settings, and Observability are the governance layer: they decide who has access, which models are configured, what events are emitted, and whether the system is healthy.',
      'A useful evaluation should not ask only whether the AI can answer a prompt. It should ask whether the platform can support the actual operating pattern: data access, permissions, review, logging, cost visibility, and failure diagnosis.',
    ],
  },
  {
    id: 'landing-whats-possible',
    title: 'What You Can Build With It',
    location: 'Public what is possible page',
    routePatterns: ['/whats-possible'],
    category: 'landing',
    audience: 'Prospects looking for use cases',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Translate the platform into concrete implementation patterns.',
    talkingPoints: [
      'Start with a repeated business process, not with a model.',
      'Choose the right pattern: agent, workflow, ambient automation, or secure conversation.',
      'Strong implementations keep governance, observability, and source knowledge attached from the start.',
    ],
    writtenGuide: [
      'This page is about turning a broad platform into specific customer ideas. The best starting point is usually a department, a recurring decision, a document-heavy task, or a workflow that currently depends on manual expert review.',
      'If the user needs advice, drafting, analysis, or question answering, start with an agent. If the work has steps, specialists, progress tracking, or human review, use a workflow. If work should begin when something changes in another system, use ambient automation. If another agent or external system needs to communicate with OrchestratorAI, use Secure Conversations.',
      'A strong idea includes the source data, the user role, the desired output, the review step, and the operational risk. That is the difference between a demo and a system someone can actually run.',
    ],
  },
  {
    id: 'landing-pricing',
    title: 'How Pricing Should Be Understood',
    location: 'Public pricing page',
    routePatterns: ['/pricing'],
    category: 'landing',
    audience: 'Buyers and procurement stakeholders',
    status: 'published',
    loomUrl: null,
    readTime: '2 min read',
    purpose: 'Frame pricing around implementation scope and operational value.',
    talkingPoints: [
      'Pricing should be tied to real deployed use, not generic AI access.',
      'Scope depends on users, workflows, integrations, data sources, and governance needs.',
      'The right buying question is which business capability should become measurable and repeatable.',
    ],
    writtenGuide: [
      'Pricing for this platform should be understood as platform deployment plus implementation scope. The value is not just access to a model; the value is turning a business process into a governed AI capability.',
      'The meaningful sizing questions are: how many users need access, which product areas are needed, what documents or systems are involved, which workflows must be customized, and what security or observability expectations apply.',
      'A small team may only need a few focused agents and a RAG collection. A larger implementation may need multiple workflows, role controls, ambient automation, model cost tracking, and external agent communication.',
    ],
  },
  {
    id: 'landing-about',
    title: 'About the Platform Architecture',
    location: 'Public about page',
    routePatterns: ['/about'],
    category: 'landing',
    audience: 'Evaluators who want to understand the operating model',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Explain the company values and architecture callout on the About page.',
    talkingPoints: [
      'The About page frames OrchestratorAI as a platform product, not a collection of disconnected demos.',
      'The architecture section shows the unified web app, API backend, modules, A2A protocol, and ExecutionContext traceability.',
      'Use this page when a stakeholder needs confidence that the product has an operating foundation.',
    ],
    writtenGuide: [
      'The About page explains how OrchestratorAI Enterprise is intended to be built and operated. The values section describes the building posture: production-grade systems, clear operating boundaries, and AI that can be traced and governed.',
      'The architecture callout is the important technical signal. OrchestratorAI Enterprise runs as a unified platform web app and API backend, with focused modules for Agents, Workflows, Ambient automation, Secure Conversations, and platform administration.',
      'Agent communication is designed around the A2A protocol. Every agent call carries an ExecutionContext so the platform can trace who ran the work, which organization it belonged to, which model was used, and how the run should be attributed.',
      'Use this page when a buyer, builder, or reviewer needs the big architectural picture before looking at a specific module.',
    ],
  },
  {
    id: 'landing-videos',
    title: 'Using the Learning Library',
    location: 'Public learning page',
    routePatterns: ['/videos'],
    category: 'landing',
    audience: 'Evaluators who want a guided learning path',
    status: 'published',
    loomUrl: null,
    readTime: '1 min read',
    purpose: 'Explain the public learning area without making video a dependency.',
    talkingPoints: [
      'Use the public learning area for broad orientation.',
      'Use in-app Help for page-specific explanation.',
      'Optional videos can support the written help later, but the article is the primary reference.',
    ],
    writtenGuide: [
      'This page is the public learning area for OrchestratorAI Enterprise. It is useful before someone signs in or when a stakeholder wants a broad overview.',
      'Inside the app, Help is more precise. It explains the exact page or function currently on screen.',
      'If short videos are added later, they should reinforce the written help rather than replace it. The written explanation remains the reliable reference.',
    ],
  },
  {
    id: 'app-dashboard',
    title: 'Command Dashboard Orientation',
    location: 'Authenticated dashboard',
    routePatterns: ['/app/dashboard'],
    category: 'home',
    audience: 'All authenticated users',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Help users choose the correct product area after login.',
    talkingPoints: [
      'The dashboard is the command center for entitled product areas.',
      'Visible cards depend on organization entitlements, user roles, and view mode.',
      'Choose the area based on the kind of work: direct conversation, structured workflow, automation, external communication, knowledge, or administration.',
    ],
    writtenGuide: [
      'The dashboard is the authenticated starting point. It shows the product areas available to the current user and organization. If a card is missing, the cause is usually entitlement, role permission, or view mode.',
      'Agents are for direct work with specialized AI workers. Workflows are for repeatable multi-step processes. Ambient is for event-driven background automation. Secure Conversations is for external agent-to-agent communication. Admin, RAG, and Settings are operational controls.',
      'The dashboard is not meant to explain every detail. It is a routing surface. Use it to pick the right part of the platform, then use the Help article inside that section for deeper context.',
    ],
  },
  {
    id: 'agents-list',
    title: 'What Agents Are',
    location: 'Agents list',
    routePatterns: ['/app/agents'],
    category: 'agents',
    audience: 'Business users choosing an agent',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain agents as focused, task-oriented AI workers inside the platform.',
    talkingPoints: [
      'Agents are specialized workers, not generic chat boxes.',
      'The list is grouped by organization, with General/global agents shown separately.',
      'Use Workflows instead when the job has stages, review gates, or long-running state.',
    ],
    writtenGuide: [
      'An agent is a focused AI worker for a specific kind of task. A good agent has a role, instructions, model configuration, optional tools, and a clear output expectation. The point is specialization, not another generic chat window.',
      'This page lists available agents and groups them by organization. The app loads the agent roster for the current organization, and non-super-admin users are moved into one of their organizations when needed. General or global agents are shown separately so shared capabilities do not get confused with organization-specific ones.',
      'Agent types are implemented through runner families. Context agents rely on conversation and instructions. RAG agents retrieve from managed collections. API agents call configured services. External agents coordinate with outside systems. Media agents handle image, audio, or video-like generation and processing.',
      'Choose an agent when the user needs a focused conversation or artifact. Choose a workflow when the work has multiple stages, multiple specialist roles, visible progress, review gates, or a durable result that should be reproducible.',
    ],
  },
  {
    id: 'agent-conversation',
    title: 'Working With an Agent Conversation',
    location: 'Agent conversation page',
    routePatterns: ['/app/agents/:agentSlug/conversation'],
    category: 'agents',
    audience: 'End users chatting with an agent',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Teach users how to work with an individual agent and understand what the page is doing.',
    talkingPoints: [
      'The page initializes an ExecutionContext for the selected agent, organization, user, provider, and model.',
      'The first prompt should include context, constraints, source material, and the desired output.',
      'Attachments and voice mode are available when the selected agent and input support them.',
    ],
    writtenGuide: [
      'This page is the live workspace for a single selected agent. Before a message is sent, the app resolves the current organization, user, conversation ID, agent slug, agent type, provider, and model into an ExecutionContext. That context is what lets the platform attribute work correctly.',
      'The welcome state shows the selected agent name and prompt box. Once messages exist, the page switches to the conversation thread and keeps the input pinned at the bottom. The conversation can load existing history when a conversation ID is present in the route.',
      'Good results start with a specific first prompt. Tell the agent what you are trying to accomplish, what constraints matter, what format you want back, and what source material it should use. If the agent needs files, attach them instead of asking the model to guess.',
      'Media agents use different default model behavior than text agents. Voice mode can transcribe input and speak responses. If a message fails, the error appears in the thread because failures should be visible rather than hidden.',
    ],
  },
  {
    id: 'agents-pipeline',
    title: 'Agent Types and Build Pipeline',
    location: 'Agent pipeline builder',
    routePatterns: ['/app/agents/pipeline'],
    category: 'agents',
    audience: 'Builders and administrators',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain how agent behavior is assembled from reusable runner families.',
    talkingPoints: [
      'The left panel lists available runners; the right panel shows the pipeline being assembled.',
      'Runner families cover context, RAG, API, external, and media work.',
      'A saved pipeline turns informal behavior into a repeatable execution pattern.',
    ],
    writtenGuide: [
      'The Build Custom Pipeline page is for composing repeatable agent behavior. The left panel lists available runners. The right panel shows Your Pipeline. Add runners, remove them, reorder them, and save when the sequence represents the behavior you want.',
      'Runner families matter because they have different operational risks. Context runners depend on strong instructions and user prompts. RAG runners depend on collection quality. API runners depend on configured services and permissions. External runners depend on integration boundaries. Media runners depend on clear output expectations and model capability.',
      'Use this page when a behavior is important enough to repeat. If an agent always needs retrieval, a tool call, validation, or media generation, that behavior should be represented as a pipeline rather than left as tribal knowledge in a prompt.',
      'Saving a pipeline uses the current ExecutionContext, so the pipeline belongs to the same organizational and user context as the rest of agent execution.',
    ],
  },
  {
    id: 'workflow-marketing-swarm',
    title: 'Marketing Swarm Workflow',
    location: 'Marketing Swarm workflow',
    routePatterns: ['/app/workflows/marketing-swarm'],
    category: 'workflows',
    audience: 'Workflow users and demo viewers',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain how a multi-agent workflow differs from a single agent conversation.',
    talkingPoints: [
      'The workflow moves through configuration, progress, and results views.',
      'It coordinates writer, editor, and evaluator agents through a structured process.',
      'Real-time updates flow through SSE while the workflow runs.',
    ],
    writtenGuide: [
      'Marketing Swarm demonstrates the workflow pattern. It is not one assistant answering one prompt. It is a coordinated process with configuration, execution progress, and results.',
      'The configuration form collects the content type, prompt data, and swarm configuration. The workflow initializes agent card states for writers, editors, and evaluators, creates or reuses a conversation, connects an SSE stream, and starts the swarm execution.',
      'Use this workflow when the desired output benefits from multiple specialist passes. Writers generate options, editors refine them, and evaluators score or compare the results. That pattern is useful anywhere quality improves when multiple roles contribute.',
      'If the user only needs a quick answer, use an agent. If the user needs a repeatable process with progress and result structure, use a workflow.',
    ],
  },
  {
    id: 'ambient-dashboard',
    title: 'Ambient Automation Dashboard',
    location: 'Ambient dashboard',
    routePatterns: ['/app/ambient'],
    category: 'ambient',
    audience: 'Operations users and administrators',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain the ambient automation concept and the dashboard status cards.',
    talkingPoints: [
      'Ambient automation is background AI work that starts from events.',
      'Listeners observe, triggers decide, workflows act, and executions prove what happened.',
      'The dashboard shows active listeners, enabled workflows, active triggers, SSE stream state, recent trigger fires, and workflow runs.',
    ],
    writtenGuide: [
      'Ambient automation is for work that should happen in the background when something changes. It is different from a user opening an agent and asking for help.',
      'The mental model is simple: listeners watch systems, triggers decide whether an event matters, workflows perform the work, and executions record the result. The dashboard summarizes those parts so an operator can see whether the automation system is alive.',
      'The top cards show active listeners, enabled workflows, active triggers, and the SSE stream connection. Listener Status shows which listeners are connected. Recent Trigger Fires shows recent trigger outcomes, and Recent Workflow Runs shows workflow execution history.',
      'This system should be treated conservatively. If a listener is noisy or a trigger is vague, automation becomes hard to trust. Use the dashboard to understand health and scope before drilling into the detailed pages.',
    ],
  },
  {
    id: 'ambient-listeners',
    title: 'Ambient Listeners',
    location: 'Ambient listeners',
    routePatterns: ['/app/ambient/listeners'],
    category: 'ambient',
    audience: 'Automation builders and testers',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Explain registered listeners and the event simulation tools.',
    talkingPoints: [
      'Listeners are the input side of ambient automation.',
      'The page shows listener active state, type, firing count, and last fired time.',
      'Simulation tools create database or file events for development and testing.',
    ],
    writtenGuide: [
      'Listeners watch source systems and convert changes into events. A database listener might observe inserts, updates, or deletes. A file listener might observe created, modified, or deleted files.',
      'The Registered Listeners panel shows whether each listener is active, what type it is, how many times it has fired, and when it last fired. If automation is not starting, this is one of the first places to check.',
      'The simulation tools are for development and testing. Database simulation asks for a table name and event type. File simulation asks for a path and file event. These tools help prove the listener-trigger-workflow chain without waiting for a real external event.',
    ],
  },
  {
    id: 'ambient-workflows',
    title: 'Ambient Workflows',
    location: 'Ambient workflows',
    routePatterns: ['/app/ambient/workflows'],
    category: 'ambient',
    audience: 'Automation builders and operators',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Explain workflow definitions and run history in ambient automation.',
    talkingPoints: [
      'Defined Workflows are the actions available to ambient triggers.',
      'Run Now manually executes a workflow for testing or operations.',
      'Run History shows workflow ID, trigger source, status, and start time.',
    ],
    writtenGuide: [
      'Ambient workflows are the work units that run when a trigger decides action is needed. A workflow definition should have a clear name, description, trigger relationship, and enabled state.',
      'The Defined Workflows section lists registered workflows and exposes Run Now for manual execution. Manual runs are useful when testing a workflow independently from listener and trigger behavior.',
      'Run History shows previous executions with status. When debugging automation, compare the workflow run history with trigger executions. If triggers fire but workflows do not run, the issue is in routing or workflow configuration. If workflows run but produce poor output, inspect the workflow itself.',
    ],
  },
  {
    id: 'ambient-triggers',
    title: 'Ambient Triggers',
    location: 'Ambient triggers',
    routePatterns: ['/app/ambient/triggers'],
    category: 'ambient',
    audience: 'Automation builders and reviewers',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain how triggers decide when background automation should act.',
    talkingPoints: [
      'Triggers connect a source event to an action configuration.',
      'Supported source types include database, file system, and schedule.',
      'A trigger requires a single organization, target agent slug, source config, and cooldown.',
    ],
    writtenGuide: [
      'Triggers are where ambient automation becomes decision-making. A listener may observe many events, but a trigger decides which events are meaningful and what action should follow.',
      'The create form asks for a name, source type, source-specific configuration, target agent slug, optional message template, and cooldown. Database triggers require a table name. File system triggers require a path. Schedule triggers require a cron expression.',
      'The page intentionally requires a single organization before creating a trigger. That prevents ambiguous automation ownership. The target agent slug tells the system which agent should handle the action.',
      'Good triggers are precise. A vague trigger creates noisy automation and weak trust. Use cooldowns to avoid repeated firings when a source produces rapid bursts of events.',
    ],
  },
  {
    id: 'ambient-executions',
    title: 'Ambient Executions',
    location: 'Ambient executions',
    routePatterns: ['/app/ambient/executions'],
    category: 'ambient',
    audience: 'Operators and support users',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Show how to review the audit trail for trigger firings.',
    talkingPoints: [
      'Executions are the audit trail for trigger activity.',
      'Summary cards separate total, completed, failed, and skipped executions.',
      'Click a row to inspect ExecutionContext, source event, and A2A response when available.',
    ],
    writtenGuide: [
      'Executions show what ambient automation actually did. This page is the operating history for trigger firings and their outcomes.',
      'The summary row gives quick counts for total, completed, failed, and skipped executions. The history list shows trigger name, source type, fired time, status, and duration.',
      'Clicking an execution opens an inline detail panel. When available, it shows the ExecutionContext, source event payload, and A2A response. That makes this page the best place to understand why automation acted, skipped, failed, or produced a specific response.',
      'Do not treat failures as noise. Failures are the signal that a listener, trigger, workflow, model, or downstream dependency needs attention.',
    ],
  },
  {
    id: 'ambient-scenarios',
    title: 'Ambient Scenarios',
    location: 'Ambient scenarios',
    routePatterns: ['/app/ambient/scenarios', '/app/ambient/scenarios/:id'],
    category: 'ambient',
    audience: 'Demo users and automation designers',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Explain scenarios as guided training and verification flows.',
    talkingPoints: [
      'Scenarios teach ambient automation patterns through guided steps.',
      'The list shows category, difficulty, and step count.',
      'Scenario detail tracks the current step, expected outcome, and completion state.',
    ],
    writtenGuide: [
      'Scenarios are training and verification flows. They make ambient automation easier to understand by breaking a pattern into observe, trigger, and verify steps.',
      'The scenario list shows the name, description, category, difficulty, and number of steps. The detail page walks through each step, highlights the current step, and can show training notes and outcome verification.',
      'Use scenarios before connecting high-impact automation. They help prove that the listener, trigger, workflow, execution history, and event stream tell a coherent story.',
    ],
  },
  {
    id: 'ambient-stream',
    title: 'Ambient Event Stream',
    location: 'Ambient event stream',
    routePatterns: ['/app/ambient/stream'],
    category: 'ambient',
    audience: 'Technical operators',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Explain real-time SSE visibility for ambient automation.',
    talkingPoints: [
      'The stream connects to Ambient API SSE events.',
      'It shows event time, event type, and JSON data.',
      'Use it to verify live listener and workflow movement.',
    ],
    writtenGuide: [
      'The Event Stream page shows real-time Server-Sent Events from the Ambient API. It displays connection status, the number of buffered events, event timestamp, event type, and JSON payload.',
      'This is a technical observability surface. It is useful when verifying that a listener fired, a workflow triggered, or the SSE connection is alive.',
      'The page also documents the platform-standard SSE format: GET /api/ambient/streaming/events with text/event-stream response data. Use this when building or debugging clients that consume ambient events.',
    ],
  },
  {
    id: 'secure-conversations-overview',
    title: 'Secure Conversations Overview',
    location: 'Secure Conversations overview',
    routePatterns: ['/app/secure-conversations'],
    category: 'secure-conversations',
    audience: 'Security, integration, and platform stakeholders',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain the external A2A gateway and its trust boundary.',
    talkingPoints: [
      'Secure Conversations handles inbound and outbound agent-to-agent communication with external agents.',
      'The overview shows API status, registered external agents, SSE clients, quick navigation, and the A2A flow.',
      'The boundary applies origin validation, request signing, rate limiting, replay protection, routing, and observability.',
    ],
    writtenGuide: [
      'Secure Conversations is the external A2A gateway. It lets OrchestratorAI communicate with external agents while keeping a controlled trust boundary.',
      'The overview page checks API health, counts registered external agents, counts SSE clients, and links to Registry, Inbound A2A, Outbound A2A, and Security Monitor. The flow diagram shows the path from external agent to Secure Validator, Secure Router, internal Workflows or Agents, and JSON-RPC response.',
      'This area is not just chat and not just an API client. It is about controlled interoperability: known agents, signed requests, origin checks, rate limits, routing, message history, and auditability.',
      'Use this area when the platform needs to interoperate with outside systems without losing identity, authorization, traceability, or operational control.',
    ],
  },
  {
    id: 'secure-conversations-registry',
    title: 'External Agent Registry',
    location: 'Secure Conversations registry',
    routePatterns: ['/app/secure-conversations/registry', '/app/secure-conversations/registry/agents/:id'],
    category: 'secure-conversations',
    audience: 'Integration administrators',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain external agent discovery, registration, trust, and detail inspection.',
    talkingPoints: [
      'The registry persists known external agents in the database.',
      'Discovery fetches /.well-known/agent.json from an external URL.',
      'Agent cards show status, description, URL, version, capabilities, trust level, trust score, and last-seen metadata.',
    ],
    writtenGuide: [
      'The registry is where external agents become known entities instead of anonymous endpoints. Secure Conversations can only govern what it knows about.',
      'Use Discover External Agent to enter an external agent URL. The system fetches /.well-known/agent.json and registers the agent origin as trusted when discovery succeeds. That origin then appears in the security allow list.',
      'Agent cards show status, description, URL, version, capabilities, trust level, trust score, and last-seen information. The detail page exposes the raw agent JSON for deeper inspection.',
      'Do not treat discovery as a rubber stamp. A registered agent should have a clear identity, understandable capability list, sensible trust score, and an origin that belongs to the system you expect.',
    ],
  },
  {
    id: 'secure-conversations-inbound',
    title: 'Inbound A2A Messages',
    location: 'Secure Conversations inbound',
    routePatterns: ['/app/secure-conversations/inbound'],
    category: 'secure-conversations',
    audience: 'Integration operators',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Explain incoming external agent traffic and live monitoring.',
    talkingPoints: [
      'Inbound shows persisted message history for requests from external agents.',
      'The page connects to the Secure Conversations event stream and refreshes on inbound events.',
      'Filters can narrow by agent ID and status.',
    ],
    writtenGuide: [
      'Inbound A2A is where external agents send requests or signals into the platform. The page is both history and monitoring: it loads persisted inbound messages and listens to the event stream for live updates.',
      'The status indicator shows Live when SSE is connected and Polling when it is not. Filters let an operator narrow message history by agent ID and status.',
      'When reviewing inbound traffic, check source identity, status, method, payload, and timing. If something was rejected or rate limited, move to Security Monitor to understand the boundary decision.',
    ],
  },
  {
    id: 'secure-conversations-outbound',
    title: 'Outbound A2A Messages',
    location: 'Secure Conversations outbound',
    routePatterns: ['/app/secure-conversations/outbound'],
    category: 'secure-conversations',
    audience: 'Integration operators',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain how to send signed requests to registered external agents.',
    talkingPoints: [
      'Outbound requires a registered target agent, JSON-RPC method, and JSON params.',
      'Send (Signed) sends the request through the secure outbound path.',
      'The response panel and outbound message history show what happened.',
    ],
    writtenGuide: [
      'Outbound A2A is where OrchestratorAI initiates communication with an external agent. The page loads registered external agents and outbound message history.',
      'The Send Request form asks for Target Agent, Method, and Params. Method is JSON-RPC style, such as agents.invoke or agent.analyze. Params must be valid JSON. The Send (Signed) button sends the request through the secure outbound path.',
      'After sending, the response panel shows the returned JSON and the outbound history refreshes. If no target agents are available, go to the Registry and discover or register an external agent first.',
      'Outbound messages should be deliberate. Before sending production data, verify destination, method, params, and whether the external agent is trusted for that kind of payload.',
    ],
  },
  {
    id: 'secure-conversations-security',
    title: 'Secure Conversation Security',
    location: 'Secure Conversations security',
    routePatterns: ['/app/secure-conversations/security'],
    category: 'secure-conversations',
    audience: 'Security reviewers and administrators',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain the security monitor, allow list, rejection stats, and protection layers.',
    talkingPoints: [
      'Security Monitor shows message stats, rejected/rate-limited counts, errors, and live security events.',
      'Allowed origins are derived from registered external agents.',
      'Protection layers include origin validation, rate limiting, request signing, and replay protection.',
    ],
    writtenGuide: [
      'The Security Monitor answers a direct question: should external communication be allowed, and what was blocked? It loads message stats, registered agents, and a live security event stream.',
      'The stats cards show total messages, inbound/outbound split, rejected or rate-limited requests, and errors. The Allowed Origins panel lists origins derived from registered external agents. If there are no registered agents, inbound origins should be rejected.',
      'The page summarizes four boundary protections. Origin validation rejects unregistered origins. Rate limiting controls request volume per agent ID. Request signing verifies HMAC-SHA256 signatures in strict mode. Replay protection blocks reused nonces inside a time window.',
      'Use this page when reviewing integrations, diagnosing rejected inbound traffic, or proving that the gateway is enforcing trust rather than blindly accepting external messages.',
    ],
  },
  {
    id: 'secure-conversations-observability',
    title: 'A2A Observability',
    location: 'Secure Conversations observability pages',
    routePatterns: [
      '/app/secure-conversations/observability',
      '/app/secure-conversations/observability/topology',
      '/app/secure-conversations/observability/timeline',
      '/app/secure-conversations/observability/metrics',
      '/app/secure-conversations/observability/audit',
    ],
    category: 'secure-conversations',
    audience: 'Operators, security reviewers, and support users',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain how to inspect A2A behavior after messages flow through the gateway.',
    talkingPoints: [
      'Message Log and Audit Trail are the main observability tabs.',
      'Message filters include source, target, and status.',
      'Selecting a message reveals method, payload, timing, source, target, and status detail.',
    ],
    writtenGuide: [
      'The observability area is how operators understand what happened across Secure Conversations after traffic moved through the gateway.',
      'The Message Log tab loads recent messages and supports filtering by source, target, and status. Selecting a message opens a detail panel with source, target, method, status, timing, and JSON payload detail.',
      'The Audit Trail is for accountability. Use it when the question is not only what happened, but what policy, security, or operational event needs to be reviewed.',
      'The related topology, timeline, metrics, and audit routes answer different questions: who talked to whom, what happened first, how much traffic moved, and which events matter for governance.',
    ],
  },
  {
    id: 'secure-conversations-tools',
    title: 'Protocol Tools and Demos',
    location: 'Secure Conversations scenarios, demo, matrix, compare, and settings',
    routePatterns: [
      '/app/secure-conversations/scenarios',
      '/app/secure-conversations/demo',
      '/app/secure-conversations/matrix',
      '/app/secure-conversations/protocol-compare',
      '/app/secure-conversations/settings',
    ],
    category: 'secure-conversations',
    audience: 'Technical evaluators and builders',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Explain technical exploration surfaces for Secure Conversations.',
    talkingPoints: [
      'Scenarios and demo mode are for guided behavior exploration.',
      'Matrix and protocol compare are for understanding protocol structure and tradeoffs.',
      'Settings should be reviewed before connecting real external traffic.',
    ],
    writtenGuide: [
      'These pages are technical exploration surfaces. They are useful for understanding protocol behavior before relying on external traffic in production.',
      'Scenarios and demo mode are best for guided walkthroughs. Matrix and protocol compare are best for understanding protocol layers, differences, and tradeoffs. Settings is where local secure conversation behavior should be reviewed before real integrations are connected.',
      'Use these tools to educate builders and reviewers. For production troubleshooting, start with Registry, Inbound, Outbound, Security Monitor, and Observability.',
    ],
  },
  {
    id: 'admin-organizations',
    title: 'Managing Organizations',
    location: 'Admin organizations',
    routePatterns: ['/app/admin/organizations'],
    category: 'admin',
    audience: 'Platform administrators',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain organization records, tenant boundaries, and the organization management UI.',
    talkingPoints: [
      'Organizations are tenant boundaries for users, roles, entitlements, RAG, and agent scope.',
      'The page shows total count, searchable organization cards, slug, URL, description, timestamps, and detail modal.',
      'The slug is required at creation and cannot be changed later.',
    ],
    writtenGuide: [
      'Organizations are the administrative boundary for most of the platform. Users, role assignments, product entitlements, RAG collections, and agent visibility all depend on the organization context.',
      'The page shows a total organization count, a search bar, and organization cards. Each card shows name, slug, description, URL, and creation time. Clicking a card opens detail. The pencil edits the organization, and the trash icon asks for delete confirmation.',
      'Creating an organization requires a slug and name. The slug is the stable identifier and cannot be changed later. Choose it carefully, keep it lowercase and durable, and avoid naming it after a temporary project.',
      'Deleting an organization is high-impact because other records can depend on it. Before deleting, verify that users, entitlements, RAG collections, and agent access no longer need that organization.',
    ],
  },
  {
    id: 'admin-users',
    title: 'Managing Users',
    location: 'Admin users',
    routePatterns: ['/app/admin/users'],
    category: 'admin',
    audience: 'Platform administrators',
    status: 'published',
    loomUrl: null,
    readTime: '5 min read',
    purpose: 'Explain user lifecycle, organization membership, role scope, permissions, and account actions.',
    talkingPoints: [
      'The page uses a master-detail layout: user list on the left, selected user details on the right.',
      'Role Scope controls which organization roles are being viewed or edited.',
      'A user needs identity, organization membership, roles, and entitlements to reach product areas.',
    ],
    writtenGuide: [
      'User Management controls account access and organization membership. The left panel lists users with email, display name, organization badges, and role badges. The right panel appears after selecting a user.',
      'The Role Scope selector matters. Roles are scoped to an organization, so verify the active scope before adding or removing roles. A user can belong to multiple organizations and have different roles in each.',
      'The selected user panel shows organization memberships, current scope roles, effective permissions, and available roles to add. Role chips can be removed. The page also supports creating users, adding a selected user to an organization, changing passwords, and deleting users.',
      'Access problems usually come from one of three places: the user is not in the right organization, the user does not have the needed role permission, or the organization does not have the product entitlement. Check those in that order before assuming the app is broken.',
      'Administrative user changes should be deliberate. Removing a user from an organization or deleting the user can immediately change access to agents, workflows, RAG collections, and settings.',
    ],
  },
  {
    id: 'admin-roles',
    title: 'Roles and Permissions',
    location: 'Admin roles',
    routePatterns: ['/app/admin/roles'],
    category: 'admin',
    audience: 'Administrators and security reviewers',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain RBAC roles, permission categories, and audit visibility.',
    talkingPoints: [
      'System Roles lists defined roles and identifies system roles with a lock icon.',
      'Selecting a role opens categorized permission accordions with checkboxes.',
      'Recent Activity shows audit entries for role and permission changes.',
    ],
    writtenGuide: [
      'Roles are bundles of permissions. They decide what a user can do once the user is in an organization and the organization has the relevant entitlement.',
      'The System Roles card lists roles with display name, description, role key, and system-role state. Selecting a role loads its permissions. System roles may be locked unless the current user is a super admin.',
      'Permissions are grouped by category in accordions. Each permission has a display name, internal permission key, and description. The checkbox indicates whether the selected role has that permission.',
      'Use least privilege. Give non-admin users only the permissions required for their job. Use Recent Activity to review changes after modifying roles.',
    ],
  },
  {
    id: 'admin-entitlements',
    title: 'Entitlements and Product Access',
    location: 'Admin entitlements',
    routePatterns: ['/app/admin/entitlements'],
    category: 'admin',
    audience: 'Platform administrators and account owners',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain organization-level product access and entitlement toggles.',
    talkingPoints: [
      'Entitlements are granted per organization, not per user.',
      'The page shows granted and not-granted counts after selecting an organization.',
      'Product cards expose toggles for Workflows, Agents, Ambient, Secure Conversations, and Assistant.',
    ],
    writtenGuide: [
      'Entitlements decide which product areas an organization can use. They are different from roles. A role might allow agents:execute, but the organization still needs the Agents entitlement for the product area to be available.',
      'Start by selecting an organization. The page then shows Granted Products, Not Granted, and a product grid. Each product card has a description and a toggle. Turning a toggle on grants access; turning it off revokes access.',
      'Current product entitlements include Workflows, Agents, Ambient, Secure Conversations, and Assistant. Admin, RAG, and Settings access still depends on roles and permissions, even though this page focuses on product modules.',
      'When troubleshooting missing dashboard cards, check entitlements first, then user membership and role permissions.',
    ],
  },
  {
    id: 'admin-agent-registry',
    title: 'Admin Agent Registry',
    location: 'Admin agent registry and detail',
    routePatterns: ['/app/admin/agents', '/app/admin/agents/:slug'],
    category: 'admin',
    audience: 'Agent administrators',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain the administrative view of registered internal agents.',
    talkingPoints: [
      'The registry shows total agents, product count, organization count, filters, and agent cards.',
      'Cards show slug, product, type, organization, and updated time.',
      'Detail pages show product, type, org, slug, timestamps, description, and configuration JSON.',
    ],
    writtenGuide: [
      'The Admin Agent Registry is the operational view of internal agents registered in the platform. It is different from the user-facing Agents page: this view is for inspection, filtering, and administration.',
      'The list view shows summary counts for total agents, products, and organizations. Search by slug, name, or type. Filter by product or organization. Each card shows the agent slug, product, type, org, and updated time.',
      'Clicking an agent opens detail. Detail shows overview cards for Product, Type, and Org, then slug, description, created and updated timestamps, and configuration JSON when present.',
      'Use this page when an agent appears wrong in the user-facing catalog, when model or config behavior needs inspection, or when you need to verify which product and organization own an agent.',
    ],
  },
  {
    id: 'rag-collections',
    title: 'RAG Collections',
    location: 'RAG collections',
    routePatterns: ['/app/rag/collections'],
    category: 'rag',
    audience: 'Knowledge administrators and builders',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain collection creation, organization scope, embedding settings, and retrieval hygiene.',
    talkingPoints: [
      'Collections are scoped to the selected organization.',
      'Collection cards show document count, chunk count, complexity, embedding model, status, and created time.',
      'Creation includes name, description, embedding model, chunk size, overlap, complexity type, and private-to-creator option.',
    ],
    writtenGuide: [
      'RAG collections organize the source knowledge that agents and workflows can retrieve. Start by selecting an organization; collections are scoped to that organization.',
      'The list shows collection count, total documents, total chunks, and cards for each collection. Each card shows name, description, organization, document count, chunk count, complexity, embedding model, status, and created time.',
      'When creating a collection, choose the embedding model deliberately. The UI supports local Ollama, Vertex AI, multilingual Vertex, and OpenAI embedding options. Chunk size and overlap affect retrieval behavior. The current complexity option is comprehensive, which preserves richer retrieval metadata.',
      'Good RAG starts with collection design. Use collections for coherent knowledge boundaries: department, client, policy area, product, or workflow. Do not mix unrelated documents just because upload is easy.',
    ],
  },
  {
    id: 'rag-collection-detail',
    title: 'RAG Collection Detail',
    location: 'RAG collection detail',
    routePatterns: ['/app/rag/collections/:id'],
    category: 'rag',
    audience: 'Knowledge administrators and builders',
    status: 'published',
    loomUrl: null,
    readTime: '5 min read',
    purpose: 'Explain document upload, collection stats, processing metadata, and document maintenance.',
    talkingPoints: [
      'The top card shows collection name, slug, status, document count, chunk count, tokens, embedding model, and complexity.',
      'Documents show filename, content type, size, chunks, tokens, status, and upload time.',
      'Upload supports individual files and folder selection with batch progress.',
    ],
    writtenGuide: [
      'Collection Detail is where source material is managed. The top card summarizes the collection: name, slug, description, status, document count, chunk count, total tokens, embedding model, and complexity.',
      'The Documents section shows each uploaded document with filename, content type, size, chunk count, token count, status, and upload time. Delete removes the document from the collection.',
      'Upload supports two modes. Files mode accepts PDF, DOCX, DOC, TXT, and Markdown files through click or drag-and-drop. Folder mode lets a user select a folder, choose files through a tree selector, and monitor batch progress, successes, and failures.',
      'When an agent gives weak grounded answers, inspect this page before changing prompts. Check whether the right documents are uploaded, whether they processed successfully, whether stale documents should be removed, and whether the collection boundary is too broad.',
    ],
  },
  {
    id: 'settings-health',
    title: 'System Health',
    location: 'Settings system health',
    routePatterns: ['/app/settings/system/health'],
    category: 'settings',
    audience: 'Operators and administrators',
    status: 'published',
    loomUrl: null,
    readTime: '3 min read',
    purpose: 'Explain health checks as the first operational diagnostic.',
    talkingPoints: [
      'The page shows overall platform status and per-service health.',
      'Each service card includes display name, API prefix, status, response time, and message.',
      'Failed health checks should lead to root-cause investigation, not workarounds.',
    ],
    writtenGuide: [
      'System Health is the first place to check when the platform feels down, slow, or partially broken. It calls the health service and renders an overall platform status.',
      'Each service card shows the service display name, API prefix, status pill, response time in milliseconds, and health message. The checked-at timestamp tells you when the report was generated.',
      'If this page reports a failed health check, do not hide it. Use the failed service, API prefix, response time, and message to identify the root cause.',
    ],
  },
  {
    id: 'settings-system-config',
    title: 'System Configuration',
    location: 'Settings system config',
    routePatterns: ['/app/settings/system'],
    category: 'settings',
    audience: 'Platform administrators',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain system-wide configuration keys and JSON editing.',
    talkingPoints: [
      'Configuration appears as key, description, JSON value, and last updated time.',
      'Editing opens a modal where the value must be valid JSON.',
      'System config changes can affect multiple product areas.',
    ],
    writtenGuide: [
      'System Configuration exposes platform-level configuration records. Each item shows a key, optional description, JSON value, and last-updated timestamp.',
      'Click the edit icon to open the edit modal. The key is read-only. The value must be valid JSON before it can be saved, and the UI blocks save when the JSON is invalid.',
      'Treat this page as operational configuration, not casual preference editing. A single key may affect agents, workflows, providers, or platform behavior across organizations.',
      'After changing a configuration value, verify the feature that depends on it and check observability if behavior changes unexpectedly.',
    ],
  },
  {
    id: 'settings-llm',
    title: 'LLM Usage, Models, and Costs',
    location: 'Settings LLM pages',
    routePatterns: ['/app/settings/llm/usage', '/app/settings/llm/models', '/app/settings/llm/costs'],
    category: 'settings',
    audience: 'AI platform owners and finance stakeholders',
    status: 'published',
    loomUrl: null,
    readTime: '5 min read',
    purpose: 'Explain model operations, provider choices, token usage, reasoning visibility, and spend.',
    talkingPoints: [
      'Usage shows aggregate requests, tokens, agents, models, and a detailed per-row usage log.',
      'Models groups configured models by provider and supports adding models plus editing pricing and status.',
      'Costs connects model activity to spend accountability.',
    ],
    writtenGuide: [
      'The LLM pages are the operating view for model usage, model availability, and cost. They help answer who used which provider/model, how many tokens were consumed, and what that means financially.',
      'LLM Usage has summary cards for total requests, total tokens, agents, and models. The aggregate table can be filtered by agent and model. The detailed usage log adds filters for org slug, agent name, provider, model, date range, and reasoning availability. Rows can expand when reasoning data exists.',
      'LLM Models groups configured models by provider. The master panel shows providers, model count, usage count, and enabled count. The detail table shows model slug, display name, context window, input/output cost per 1k, usage, last used, and status. Costs can be edited inline, and models can be added through the Add Model action.',
      'LLM Costs is for spend accountability. Use these pages together: Usage explains activity, Models explains configuration and pricing, Costs explains financial impact.',
    ],
  },
  {
    id: 'settings-observability',
    title: 'Platform Observability',
    location: 'Settings observability dashboard and events',
    routePatterns: ['/app/settings/observability', '/app/settings/observability/events'],
    category: 'settings',
    audience: 'Operators, administrators, and support users',
    status: 'published',
    loomUrl: null,
    readTime: '4 min read',
    purpose: 'Explain platform event inspection and detailed event review.',
    talkingPoints: [
      'The observability dashboard summarizes platform behavior.',
      'The Event Log supports search, product filter, severity filter, paging, and detail modal.',
      'Event detail includes time, severity, type, product, org, user, agent, conversation, message, and metadata.',
    ],
    writtenGuide: [
      'Observability is how operators understand what the platform is doing under the surface. Use it before guessing at causes.',
      'The Event Log is the detailed view. Search message text, filter by product, filter by severity, and load more when additional records exist. Rows show time, severity, product, event type, message, and organization.',
      'Clicking an event opens the detail modal. Detail can include user ID, agent slug, conversation ID, full message, and metadata JSON. That makes it useful for support, audit review, and root-cause analysis.',
      'When something is confusing, look for the emitted event trail before changing configuration or code.',
    ],
  },
  {
    id: 'settings-infrastructure',
    title: 'MCP Servers and Database Settings',
    location: 'Settings infrastructure pages',
    routePatterns: ['/app/settings/mcp', '/app/settings/database'],
    category: 'settings',
    audience: 'Technical administrators',
    status: 'published',
    loomUrl: null,
    readTime: '5 min read',
    purpose: 'Explain infrastructure status for MCP tools and database connectivity.',
    talkingPoints: [
      'MCP Servers shows server connection, protocol version, tool count, namespaces, search, and tool parameter detail.',
      'Database Admin shows connection status, provider, schemas, table counts, row counts, and recent migrations.',
      'Infrastructure changes and failures can affect the whole platform.',
    ],
    writtenGuide: [
      'MCP Servers is the visibility page for the platform MCP server. When connected, it shows server name, version, protocol version, and tool count. Tools are grouped by namespace, searchable, and clickable for detail. Tool detail shows description and input parameters, including required fields.',
      'Use MCP Servers when debugging tool availability, connector exposure, or schema expectations. If the server is unavailable, the page shows an error state with retry instead of pretending tools exist.',
      'Database Admin is the visibility page for persistence. It shows connection health, provider, masked URL, schemas, available clients, table counts, row counts, and recent migrations. The table list can be filtered by table name.',
      'If the database status is unhealthy, many unrelated product areas may fail. Use the provider, health message, schemas, table list, and migration history to identify whether the issue is connection, schema drift, migration failure, or missing data.',
    ],
  },
];

function matchesRoute(pattern: string, path: string): boolean {
  if (pattern === path) return true;

  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return false;

  return patternParts.every((part, index) => part.startsWith(':') || part === pathParts[index]);
}

export function findHelpGuideForPath(path: string): HelpGuide | undefined {
  return helpGuides.find((guide) => guide.routePatterns.some((pattern) => matchesRoute(pattern, path)));
}
